import {
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MailOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Collapse,
  message,
  Popconfirm,
  Skeleton,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  cancelRenewal,
  fetchRenewalForm,
  updateRenewalStatus,
} from "../../../store/renewalsSearch/renewalsSearchThunks";
import AircraftInformationDetails from "../../applications/detail/AircraftInformationDetails";
import AirportInformationDetails from "../../applications/detail/AirportInformationDetails";
import ApplicantsDetails from "../../applications/detail/ApplicantsDetails";
import CollapseSectionHeader from "../../applications/detail/CollapseSectionHeader";
import OperationsDetails from "../../applications/detail/OperationsDetails";
import PilotInformationDetails from "../../applications/detail/PilotInformationDetails";
import ApplicationHeaderBar from "../../applications/shared/ApplicationHeaderBar";
import CancelRenewalModal from "./CancelRenewalModal";
import RenewalMetaCard from "./RenewalMetaCard";
import RenewalStatusAlert from "./RenewalStatusAlert";

const RenewalView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renewalId } = useParams();

  const data = useSelector((state) => state.renewalsSearch.viewRenewal.data);
  const isLoading = useSelector(
    (state) => state.renewalsSearch.viewRenewal.isLoading
  );
  const isSendingEmail = useSelector(
    (state) => state.renewalsSearch.viewRenewal.isSendingEmail
  );
  const isCancelling =
    useSelector((s) => s.renewalsSearch?.viewRenewal?.isCancelling) ?? false;

  const isCancelled = data?.status === "cancelled";
  const isCompleted = data?.status === "completed";
  const isSent = data?.status === "sent";
  const deliveryNeedsReview = Boolean(data?.email_delivery_state);
  const previousApplicationId = data?.previous_application_id;

  const [cancelOpen, setCancelOpen] = useState(false);
  // Goes up when Compose Email is clicked before a coverage date is set, so the date field can point itself out
  const [coverageDateAttention, setCoverageDateAttention] = useState(0);

  useEffect(() => {
    if (renewalId) {
      dispatch(fetchRenewalForm({ id: renewalId }));
    }
  }, [dispatch, renewalId]);

  const handleConfirmCancel = (reason) => {
    dispatch(cancelRenewal({ id: renewalId, reason }));
    setCancelOpen(false);
  };

  // The email page opens in one of three modes; the button says which one it will show
  const emailAction = deliveryNeedsReview
    ? { label: "Review Email Delivery", icon: <ExclamationCircleOutlined />, type: "primary" }
    : isSent
      ? { label: "View Sent Email", icon: <MailOutlined />, type: "default" }
      : { label: "Compose Email", icon: <EditOutlined />, type: "primary" };

  const headerActions = [
    <Button size="small" onClick={() => navigate(-1)}>
      <ArrowLeftOutlined /> Go Back
    </Button>,
    !isCompleted && !isCancelled && !deliveryNeedsReview && (
      <Button
        size="small"
        danger
        onClick={() => setCancelOpen(true)}
        loading={isCancelling}
      >
        Cancel
      </Button>
    ),
    isCompleted && (
      <Button
        key="view-application"
        size="small"
        type="primary"
        icon={<EyeOutlined />}
        onClick={() => {
          if (!data?.id) {
            message.error("No received application found for this renewal.");
          } else {
            navigate(`/applications/view/${data?.id}`);
          }
        }}
      >
        View Received Application
      </Button>
    ),
    isCancelled && (
      <Popconfirm
        title={"Restore renewal"}
        description={
          "This renewal has previously been cancelled. Would you like to restore it?"
        }
        onConfirm={() => {
          dispatch(updateRenewalStatus({ id: renewalId, status: "not_sent" }));
        }}
        okText="Yes"
        cancelText="No"
      >
        <Button key="restore-renewal" size="small" icon={<ReloadOutlined />}>
          Restore Renewal
        </Button>
      </Popconfirm>
    ),
    !isCompleted && !isCancelled && previousApplicationId && (
      <Button
        key="view-previous-quote"
        size="small"
        type="default"
        onClick={() => {
          if (!previousApplicationId) {
            message.error("Previous application data is unavailable.");
          } else {
            navigate(`/renewals/view-previous-quote/${previousApplicationId}`);
          }
        }}
      >
        View Previous Quote
      </Button>
    ),
    !isCompleted && !isCancelled && (
      <Button
        key="download"
        size="small"
        type="dashed"
        onClick={() => {
          if (!data.coverage_date) {
            message.error(
              "Please set a coverage date before copying the link."
            );
          } else {
            message.success("Application link copied to clipboard.");
            const applicationLink = `${window.location.origin}/all-forms/private-aviation/${data.id}`;
            navigator.clipboard.writeText(applicationLink);
          }
        }}
      >
        <CopyOutlined /> Copy application link
      </Button>
    ),
    !isCompleted && !isCancelled && (
      <Button
        key="send-email"
        size="small"
        type={emailAction.type}
        icon={emailAction.icon}
        loading={isSendingEmail}
        disabled={isSendingEmail}
        onClick={() => {
          if (!data.coverage_date && !isSent && !deliveryNeedsReview) {
            message.error(
              "Please set a coverage date before sending an email."
            );
            setCoverageDateAttention((n) => n + 1);
          } else {
            navigate(`/renewals/send-email/${renewalId}`);
          }
        }}
      >
        {emailAction.label}
      </Button>
    ),
  ];

  const items = useMemo(() => {
    return [
      {
        key: "applicants",
        label: (
          <CollapseSectionHeader
            name="Applicants Information"
            route="applicants"
            parent="renewals"
            id={data?.id}
          />
        ),
        children: <ApplicantsDetails data={data} />,
      },
      {
        key: "aircraft-core",
        label: (
          <CollapseSectionHeader
            name="Aircraft Information"
            route="aircraft"
            parent="renewals"
            id={data?.id}
          />
        ),
        children: <AircraftInformationDetails data={data} />,
      },
      {
        key: "ops-trips",
        label: (
          <CollapseSectionHeader
            name="Operations & Trips"
            route="operations"
            parent="renewals"
            id={data?.id}
          />
        ),
        children: <OperationsDetails data={data} />,
      },
      {
        key: "airport",
        label: (
          <CollapseSectionHeader
            name="Airport Information"
            route="airport"
            parent="renewals"
            id={data?.id}
          />
        ),
        children: <AirportInformationDetails data={data} />,
      },
      {
        key: "pilots",
        label: (
          <CollapseSectionHeader
            name="Pilot Information"
            route="pilot"
            parent="renewals"
            id={data?.id}
          />
        ),
        children: <PilotInformationDetails data={data} />,
      },
    ];
  }, [data]);

  return !isLoading && !data ? (
    <Alert
      type="error"
      showIcon
      message="Error: Something went wrong while loading this application."
      className="default-margin"
    />
  ) : (
    <Card className="body-card-content-no-min-height">
      <Skeleton active loading={isLoading}>
        <RenewalStatusAlert data={data} />
        <ApplicationHeaderBar data={data} actions={headerActions} isRenewal />
        <RenewalMetaCard coverageDateAttention={coverageDateAttention} />
        {!isCompleted && !isCancelled && (
          <>
            <Collapse defaultActiveKey={[]} items={items} />
          </>
        )}

        <CancelRenewalModal
          open={cancelOpen}
          onCancel={() => setCancelOpen(false)}
          onConfirm={handleConfirmCancel}
          loading={isCancelling}
        />
      </Skeleton>
    </Card>
  );
};

export default RenewalView;
